import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import {
  acceptDistillProposal,
  type DistillProposal,
  getDistillProposal,
  rejectDistillProposal,
} from '../api/files'
import Button from './Button'
import Modal from './Modal'

interface TranscriptDistillModalProps {
  fileId: number
  isOpen: boolean
  onClose: () => void
  onAccepted?: () => void
  onRejected?: () => void
}

export default function TranscriptDistillModal({
  fileId,
  isOpen,
  onClose,
  onAccepted,
  onRejected,
}: TranscriptDistillModalProps) {
  const [proposal, setProposal] = useState<DistillProposal | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [acting, setActing] = useState(false)
  const [expandedSegments, setExpandedSegments] = useState<Set<number>>(new Set())
  const [showDropped, setShowDropped] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    let cancelled = false
    setLoading(true)
    setError(null)
    setProposal(null)
    getDistillProposal(fileId)
      .then((res) => {
        if (!cancelled) setProposal(res)
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load proposal')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [isOpen, fileId])

  const toggleSegment = (idx: number) => {
    setExpandedSegments((prev) => {
      const next = new Set(prev)
      if (next.has(idx)) next.delete(idx)
      else next.add(idx)
      return next
    })
  }

  const handleAccept = async () => {
    if (!proposal) return
    setActing(true)
    try {
      const result = await acceptDistillProposal(fileId, proposal.id)
      toast.success(`Distilled: ${result.chunkCount} chunks replaced`)
      onAccepted?.()
      onClose()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Accept failed')
    } finally {
      setActing(false)
    }
  }

  const handleReject = async () => {
    if (!proposal) return
    setActing(true)
    try {
      await rejectDistillProposal(fileId, proposal.id)
      toast.success('Proposal rejected')
      onRejected?.()
      onClose()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Reject failed')
    } finally {
      setActing(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Review Transcript Distillation" size="xl">
      <div className="space-y-4">
        {loading && <p className="text-harbor-text-secondary text-sm">Loading proposal…</p>}
        {error && <p className="text-amber-400 text-sm">{error}</p>}

        {proposal && (
          <>
            {/* Header stats */}
            <div className="flex flex-wrap gap-3 text-sm">
              <span className="bg-harbor-surface-raised px-2 py-1 rounded">
                {proposal.stats.rawChunks} chunks → {proposal.stats.segments} segments
              </span>
              <span className="bg-harbor-surface-raised px-2 py-1 rounded">
                {proposal.stats.decisions} decisions
              </span>
              <span className="bg-harbor-surface-raised px-2 py-1 rounded">
                {proposal.stats.actionItems} action items
              </span>
              <span className="bg-emerald-900/30 text-emerald-400 px-2 py-1 rounded">
                {proposal.stats.reductionPct}% smaller
              </span>
              {proposal.stats.dropped > 0 && (
                <span className="bg-harbor-surface-raised px-2 py-1 rounded text-harbor-text-muted">
                  {proposal.stats.dropped} segments dropped
                </span>
              )}
            </div>

            {/* Decisions & Action Items */}
            {(proposal.payload.decisions.length > 0 || proposal.payload.actionItems.length > 0) && (
              <div className="space-y-2">
                <h3 className="text-sm font-medium text-harbor-text">Extracted Items</h3>
                <div className="max-h-48 overflow-y-auto space-y-1">
                  {proposal.payload.decisions.map((d, i) => (
                    <div key={`d-${i}`} className="text-sm bg-harbor-surface-raised p-2 rounded">
                      <span className="text-emerald-400 font-medium">Decision: </span>
                      <span className="text-harbor-text">{d.statement}</span>
                      {d.owner && <span className="text-harbor-text-muted ml-2">({d.owner})</span>}
                      {d.quote && (
                        <p className="text-harbor-text-muted text-xs mt-1 italic">"{d.quote}"</p>
                      )}
                    </div>
                  ))}
                  {proposal.payload.actionItems.map((a, i) => (
                    <div key={`a-${i}`} className="text-sm bg-harbor-surface-raised p-2 rounded">
                      <span className="text-amber-400 font-medium">Action: </span>
                      <span className="text-harbor-text">{a.statement}</span>
                      {a.owner && (
                        <span className="text-harbor-text-muted ml-2">
                          ({a.owner}
                          {a.due ? `, ${a.due}` : ''})
                        </span>
                      )}
                      {a.quote && (
                        <p className="text-harbor-text-muted text-xs mt-1 italic">"{a.quote}"</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Segments */}
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-harbor-text">
                Segments ({proposal.payload.segments.length})
              </h3>
              <div className="max-h-64 overflow-y-auto space-y-1">
                {proposal.payload.segments.map((seg) => (
                  <div
                    key={seg.sourceChunkIndex}
                    className="text-sm bg-harbor-surface-raised p-2 rounded"
                  >
                    <button
                      type="button"
                      className="w-full text-left flex items-center justify-between"
                      onClick={() => toggleSegment(seg.sourceChunkIndex)}
                    >
                      <span className="text-harbor-text font-medium">
                        Segment {seg.sourceChunkIndex}
                      </span>
                      <span className="text-harbor-text-muted text-xs">
                        {expandedSegments.has(seg.sourceChunkIndex) ? '▾' : '▸'}
                      </span>
                    </button>
                    <p className="text-harbor-text-secondary mt-1 whitespace-pre-wrap">
                      {seg.notes}
                    </p>
                    {expandedSegments.has(seg.sourceChunkIndex) && seg.rawTurns && (
                      <div className="mt-2 pt-2 border-t border-harbor-border">
                        <p className="text-xs text-harbor-text-muted font-medium mb-1">Verbatim:</p>
                        <p className="text-harbor-text-muted text-xs whitespace-pre-wrap">
                          {seg.rawTurns}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Dropped segments */}
            {proposal.payload.droppedSegments.length > 0 && (
              <div className="space-y-2">
                <button
                  type="button"
                  className="text-sm text-harbor-text-muted hover:text-harbor-text"
                  onClick={() => setShowDropped(!showDropped)}
                >
                  {showDropped ? '▾' : '▸'} {proposal.payload.droppedSegments.length} segments
                  dropped
                </button>
                {showDropped && (
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {proposal.payload.droppedSegments.map((seg) => (
                      <div
                        key={seg.sourceChunkIndex}
                        className="text-xs bg-harbor-surface-raised p-2 rounded opacity-60"
                      >
                        <span className="line-through">
                          Segment {seg.sourceChunkIndex}: {seg.reason}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Footer */}
            <div className="flex justify-between items-center pt-2 border-t border-harbor-border">
              <p className="text-xs text-harbor-text-muted">
                Model: {proposal.stats.model} · {proposal.stats.llmTokens} tokens
              </p>
              <div className="flex gap-2">
                <Button variant="secondary" onClick={handleReject} disabled={acting}>
                  Reject
                </Button>
                <Button variant="primary" onClick={handleAccept} disabled={acting}>
                  {acting ? 'Accepting…' : 'Accept & replace chunks'}
                </Button>
              </div>
            </div>
          </>
        )}

        {!loading && !error && !proposal && (
          <p className="text-harbor-text-secondary text-sm">No proposal found.</p>
        )}
      </div>
    </Modal>
  )
}
